import React, { createContext, useState, useEffect } from 'react';
import api from '../services/api';

export const AuthContext = createContext();
const PERMISSIONS_BOOTSTRAP_KEY = 'smart_permissions_bootstrap_pending';
const PERMISSIONS_UPDATED_EVENT = 'smart-permissions-updated';

if (typeof window !== 'undefined' && localStorage.getItem('role_id')) {
  localStorage.setItem(PERMISSIONS_BOOTSTRAP_KEY, '1');
}

const notifyPermissionsUpdated = () => {
  try {
    window.dispatchEvent(new CustomEvent(PERMISSIONS_UPDATED_EVENT));
  } catch (_) {}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);

  const login = async (username, password) => {
    try {
      const response = await api.post('/login', { username, password });
      if (!response?.data?.role_id || !response?.data?.token) {
        throw new Error('Invalid login response: missing role_id or token');
      }

      console.log('✅ Login Response:', response.data);

      setUser(response.data.userId);

      // خزّن الجلسة أولًا ليعمل الـinterceptor (Authorization + X-ROLE-ID)
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userName', response.data.userName);
      localStorage.setItem('userId', response.data.userId);
      localStorage.setItem('role_id', response.data.role_id);
      if (Number(response.data.role_id) === 3 && response.data.owner_id) {
        localStorage.setItem('owner_id', response.data.owner_id);
      } else {
        localStorage.removeItem('owner_id');
      }

      // 1) جلب الصلاحيات: { page_name: true/false } ← تحويلها لمصفوفة [{page_name, can_access}]
      const permsRes = await api.get('/me/permissions');
      const permissionsData = Object.entries(
        permsRes.data.permissions || {}
      ).map(([page_name, allowed]) => ({
        page_name,
        can_access: allowed ? 1 : 0,
      }));
      localStorage.setItem('permissions', JSON.stringify(permissionsData));
      setPermissions(permissionsData);

      // 2) جلب عناصر القائمة المسموح بها + آباؤها
      const [menuRes, allMenuRes] = await Promise.all([
        api.get('/me/menu'),
        api.get('/app-menu'),
      ]);
      localStorage.setItem('me_menu', JSON.stringify(menuRes.data.items || []));
      localStorage.setItem('app_menu_all', JSON.stringify(allMenuRes.data || []));
      localStorage.removeItem(PERMISSIONS_BOOTSTRAP_KEY);
      notifyPermissionsUpdated();

      return response;
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setPermissions([]);
    localStorage.removeItem('userId');
    localStorage.removeItem('permissions');
    localStorage.removeItem('userName');
    localStorage.removeItem('token');
    localStorage.removeItem('role_id');
    localStorage.removeItem('owner_id');
    localStorage.removeItem('me_menu');
    localStorage.removeItem('app_menu_all');
    localStorage.removeItem(PERMISSIONS_BOOTSTRAP_KEY);
  };

  // ✅ عند تحديث الصفحة، تحميل الصلاحيات من LocalStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('userId');
    const storedPermissions = localStorage.getItem('permissions');
    const hasStoredRole = Boolean(localStorage.getItem('role_id'));

    if (storedUser) {
      setUser(storedUser);
    }

    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch {
        localStorage.removeItem('permissions');
      }
    }

    if (!hasStoredRole) {
      localStorage.removeItem(PERMISSIONS_BOOTSTRAP_KEY);
      return;
    }

    localStorage.setItem(PERMISSIONS_BOOTSTRAP_KEY, '1');

    let pollingInterval = null;

    Promise.allSettled([
      api.get('/me/permissions').then(({ data }) => {
        const mapped = Object.entries(data.permissions || {}).map(
          ([page_name, allowed]) => ({
            page_name,
            can_access: allowed ? 1 : 0,
          })
        );
        localStorage.setItem('permissions', JSON.stringify(mapped));
        setPermissions(mapped);
      }),
      api.get('/me/menu').then(({ data }) => {
        localStorage.setItem('me_menu', JSON.stringify(data.items || []));
      }),
      api.get('/app-menu').then(({ data }) => {
        localStorage.setItem('app_menu_all', JSON.stringify(data || []));
      }),
    ]).finally(() => {
      localStorage.removeItem(PERMISSIONS_BOOTSTRAP_KEY);
      notifyPermissionsUpdated();

      // تحديث الصلاحيات و قائمة الـ sidebar تلقائياً كل 30 ثانية لاستقبال
      // أي تغييرات يجريها الأدمن على الصلاحيات و القائمة في الوقت شبه الحقيقي.
      pollingInterval = setInterval(() => {
        if (!localStorage.getItem('role_id')) return;
        let permsChanged = false;
        let menuChanged = false;
        Promise.allSettled([
          api.get('/me/permissions').then(({ data }) => {
            const mapped = Object.entries(data.permissions || {}).map(
              ([page_name, allowed]) => ({
                page_name,
                can_access: allowed ? 1 : 0,
              })
            );
            const current = localStorage.getItem('permissions') || '[]';
            const next = JSON.stringify(mapped);
            if (current !== next) {
              localStorage.setItem('permissions', next);
              setPermissions(mapped);
              permsChanged = true;
            }
          }),
          api.get('/me/menu').then(({ data }) => {
            const items = JSON.stringify(data.items || []);
            const current = localStorage.getItem('me_menu') || '[]';
            if (current !== items) {
              localStorage.setItem('me_menu', items);
              menuChanged = true;
            }
          }),
        ]).finally(() => {
          if (permsChanged || menuChanged) {
            notifyPermissionsUpdated();
            // اطلب من الـ Sidebar إعادة بناء قائمته من الـ API مباشرةً.
            try { window.dispatchEvent(new Event('smart-menu-refresh')); } catch (_) {}
          }
        });
      }, 30 * 1000);
    });

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  useEffect(() => {
    //console.log('✅ Updated Permissions:', permissions);
  }, [permissions]);

  return (
    <AuthContext.Provider value={{ user, permissions, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
